import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Elements } from "@stripe/react-stripe-js";
import { ChevronLeft, User, Mail, Phone, ArrowRight } from "lucide-react";
import { API, LOGO_URL } from "@/config";
import { Fireflies } from "@/components/effects/Fireflies";
import { getStripe, resetStripeCache } from "./checkout/stripeClient";
import { CheckoutForm } from "./checkout/CheckoutForm";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OrderSummary } from "./checkout/OrderSummary";
import ThemedPhoneInput from "@/components/ui/ThemedPhoneInput";
import { trackBeginCheckout } from "@/utils/ga4";

/* ==================== Checkout Page shell ====================
 * Owns the /checkout data-loading lifecycle: hits /checkout/initiate
 * (fresh trip) OR /checkout/resume/:rid (phone bookings / pay-links),
 * fetches products + zones + settings, then hands off to CheckoutForm
 * (inside <Elements>) and OrderSummary (right column). Modal wrappers,
 * form logic, and payment UI all live in /pages/checkout/.
 */
export const CheckoutPage = () => {
  const { state } = useLocation();
  const [sp] = useSearchParams();
  const resumeId = sp.get("rid");

  const [reservationId, setReservationId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [quote, setQuote] = useState(null);
  const [products, setProducts] = useState([]);
  const [zones, setZones] = useState([]);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumeCustomer, setResumeCustomer] = useState(null);
  // Trip patches (dates / address) applied via the inline "Edit trip" modal
  // in the OrderSummary. Keeps the trip memo in sync without a full page nav.
  const [tripPatch, setTripPatch] = useState(null);
  const initiatedRef = useRef(false);

  // Contact-info gate — customer must enter name/email/phone BEFORE a
  // reservation record is created in the database.
  const [gateContact, setGateContact] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [gatePassed, setGatePassed] = useState(!!resumeId); // resume mode skips gate
  const [gateError, setGateError] = useState("");

  const trip = useMemo(() => {
    if (resumeCustomer) {
      const t = resumeCustomer.trip || {};
      return {
        cart: {},
        range: {
          from: t.delivery_date ? new Date(t.delivery_date + "T12:00:00") : null,
          to: t.pickup_date ? new Date(t.pickup_date + "T12:00:00") : null,
        },
        zoneId: t.zone_id,
        delivery_resort: t.delivery_resort,
        deliverySlot: t.delivery_slot,
        pickupSlot: t.pickup_slot,
        address: "", isOffProperty: false,
        differentPickup: !!t.differentPickup,
        pickup_zone_id: null, pickup_resort: t.pickup_resort,
        pickup_address: null, pickupIsOffProperty: false,
      };
    }
    if (!state) return null;
    const base = {
      ...state,
      range: {
        from: state.range?.from ? new Date(state.range.from) : null,
        to: state.range?.to ? new Date(state.range.to) : null,
      },
    };
    if (tripPatch) {
      if (tripPatch.delivery) base.range.from = new Date(`${tripPatch.delivery}T12:00:00`);
      if (tripPatch.pickup) base.range.to = new Date(`${tripPatch.pickup}T12:00:00`);
      if (tripPatch.deliveryResort) base.delivery_resort = tripPatch.deliveryResort;
      if (tripPatch.pickupResort) base.pickup_resort = tripPatch.pickupResort;
      if (tripPatch.deliverySlot) base.deliverySlot = tripPatch.deliverySlot;
      if (tripPatch.pickupSlot) base.pickupSlot = tripPatch.pickupSlot;
    }
    return base;
  }, [state, resumeCustomer, tripPatch]);

  useEffect(() => {
    if (initiatedRef.current) return;
    if (!gatePassed) return; // wait for contact-info gate
    initiatedRef.current = true;
    (async () => {
      try {
        const [prodRes, zoneRes, settingsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/zones`),
          axios.get(`${API}/settings`),
        ]);
        setProducts(prodRes.data.products || []);
        setZones(zoneRes.data.zones || []);
        setSettings(settingsRes.data);

        // Resume mode — phone booking or a returning customer via pay-link.
        if (resumeId) {
          const r = await axios.get(`${API}/checkout/resume/${resumeId}`);
          setReservationId(r.data.reservation_id);
          setClientSecret(r.data.client_secret);
          setQuote(r.data.quote);
          setResumeCustomer({ ...r.data.customer, trip: r.data.trip, damage_protection: r.data.damage_protection, address_locked: r.data.address_locked, booking_source: r.data.booking_source });
          setLoading(false);
          return;
        }

        // Fresh self-serve from Home.
        if (!trip || !trip.range.from || !trip.range.to) {
          setError("no-trip"); setLoading(false); return;
        }
        const items = Object.entries(trip.cart || {})
          .filter(([, q]) => q > 0)
          .map(([product_id, quantity]) => ({
            product_id,
            quantity,
            option_ids: (trip.cartOptions && trip.cartOptions[product_id]) || [],
            addon_for: (trip.cartAddons && trip.cartAddons[product_id]) || null,
          }));
        if (items.length === 0) { setError("no-items"); setLoading(false); return; }

        const payload = {
          zone_id: trip.zoneId,
          delivery_resort: trip.delivery_resort,
          delivery_date: format(trip.range.from, "yyyy-MM-dd"),
          delivery_slot: trip.deliverySlot,
          pickup_date: format(trip.range.to, "yyyy-MM-dd"),
          pickup_slot: trip.pickupSlot,
          items,
          origin_url: window.location.origin,
          pickup_zone_id: trip.pickup_zone_id,
          pickup_resort: trip.pickup_resort,
          pickup_address: trip.pickup_address,
          notes: trip.isOffProperty && trip.address ? `Address: ${trip.address}` : "",
          // Contact info — passed upfront so no blank reservation is stored.
          customer_name: `${gateContact.firstName} ${gateContact.lastName}`.trim(),
          customer_email: gateContact.email,
          customer_phone: gateContact.phone,
        };
        const r = await axios.post(`${API}/checkout/initiate`, payload);
        setReservationId(r.data.reservation_id);
        setClientSecret(r.data.client_secret);
        setQuote(r.data.quote);
      } catch (e) {
        setError(e.response?.data?.detail || e.message || "unknown");
      } finally { setLoading(false); }
    })();
  }, [trip, resumeId, gatePassed, gateContact]);

  // Kick off Stripe.js load in parallel with the rest of the page.
  const [stripeError, setStripeError] = useState(null); // null | 'generic' | 'key_mismatch'

  // GA4 begin_checkout — fires once when quote is first available.
  const ga4CheckoutFiredRef = useRef(false);
  useEffect(() => {
    if (!quote || ga4CheckoutFiredRef.current) return;
    ga4CheckoutFiredRef.current = true;
    // Persist cart snapshot so BookingSuccess can fire the purchase event
    // even after a Stripe redirect (sessionStorage survives same-tab redirects).
    const ref = sessionStorage.getItem("_egr_ref");
    const affiliationCode = ref ? (() => { try { return JSON.parse(ref).code; } catch(_){return undefined;} })() : undefined;
    try {
      sessionStorage.setItem("_egr_ga4_cart", JSON.stringify({
        total: quote.total,
        items: quote.line_items || [],
        coupon: quote.applied_promo || undefined,
        affiliationCode,
      }));
    } catch (_) {}
    trackBeginCheckout({
      value: quote.total,
      items: quote.line_items || [],
      coupon: quote.applied_promo || undefined,
    });
  }, [quote]);
  const stripePromise = useMemo(() => {
    const p = getStripe();
    p.catch((err) => setStripeError(err?.code === "STRIPE_KEY_MISMATCH" ? "key_mismatch" : "generic"));
    return p;
  }, []);

  if (stripeError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass p-8 max-w-lg text-center" data-testid="checkout-config-error">
          {stripeError === "key_mismatch" ? (
            <>
              <h1 className="font-serif text-2xl text-[#FFD166] mb-3">Payment temporarily unavailable</h1>
              <p className="text-sm text-[#B9C8D5] mb-4">
                We're finishing setup on our payment processor — sorry about the timing. Please text or email us and we'll get your booking locked in manually within minutes.
              </p>
              <p className="text-[10px] text-[#B9C8D5]/70 mb-4" data-testid="checkout-config-error-hint">
                Admin: Stripe publishable and secret keys don't match on this environment — check STRIPE_PUBLISHABLE_KEY and STRIPE_API_KEY are the same mode (test/live) and same Stripe account.
              </p>
            </>
          ) : (
            <p className="text-[#F4A93A] mb-4">Our secure checkout couldn't start — please refresh the page and try again.</p>
          )}
          <button onClick={() => { resetStripeCache(); window.location.reload(); }} className="btn-primary" data-testid="checkout-config-retry">Refresh</button>
        </div>
      </div>
    );
  }

  // --- Contact-info gate (new checkouts only) ---
  if (!gatePassed && !resumeId) {
    const allFilled = gateContact.firstName.trim() && gateContact.email.trim() && gateContact.phone.trim();
    const handleGateSubmit = (e) => {
      e.preventDefault();
      if (!gateContact.firstName.trim()) { setGateError("Please enter your first name."); return; }
      if (!gateContact.email.trim() || !/\S+@\S+\.\S+/.test(gateContact.email)) { setGateError("Please enter a valid email address."); return; }
      if (!gateContact.phone.trim()) { setGateError("Please enter your mobile number."); return; }
      setGateError("");
      setGatePassed(true);
    };
    return (
      <div className="min-h-screen bg-[#04090F] relative overflow-hidden flex items-start justify-center pt-16 px-4">
        <Fireflies count={12} />
        <div className="relative z-10 w-full max-w-lg">
          <Link to="/" className="flex items-center gap-2 text-[#FFD166] hover:text-[#F4A93A] text-sm mb-6">
            <ChevronLeft className="w-4 h-4" /> Back to catalog
          </Link>
          <div className="glass rounded-2xl p-8" data-testid="checkout-contact-gate">
            <img src={LOGO_URL} alt="Enchanted Gear Rentals" className="h-10 mb-6" style={{ filter: "url(#knockoutBlack)" }} />
            <h2 className="font-serif text-3xl text-[#FFD166] mb-1">Almost there!</h2>
            <p className="text-sm text-[#B9C8D5] mb-6">Enter your contact info to hold your gear and create your booking.</p>
            <form onSubmit={handleGateSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#B9C8D5] mb-1">First name *</label>
                  <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B9C8D5]" />
                  <input autoFocus value={gateContact.firstName} onChange={(e) => setGateContact((c) => ({ ...c, firstName: e.target.value }))} placeholder="Jane" className="w-full bg-[#0A1A26] border border-[#FFD166]/20 text-[#F6F1E4] rounded-lg pl-9 pr-3 py-2.5 text-sm focus:border-[#FFD166]/50 focus:outline-none" data-testid="gate-first-name" /></div>
                </div>
                <div>
                  <label className="block text-xs text-[#B9C8D5] mb-1">Last name</label>
                  <input value={gateContact.lastName} onChange={(e) => setGateContact((c) => ({ ...c, lastName: e.target.value }))} placeholder="Smith" className="w-full bg-[#0A1A26] border border-[#FFD166]/20 text-[#F6F1E4] rounded-lg px-3 py-2.5 text-sm focus:border-[#FFD166]/50 focus:outline-none" data-testid="gate-last-name" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#B9C8D5] mb-1">Email address *</label>
                <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B9C8D5]" />
                <input type="email" value={gateContact.email} onChange={(e) => setGateContact((c) => ({ ...c, email: e.target.value }))} placeholder="jane@example.com" className="w-full bg-[#0A1A26] border border-[#FFD166]/20 text-[#F6F1E4] rounded-lg pl-9 pr-3 py-2.5 text-sm focus:border-[#FFD166]/50 focus:outline-none" data-testid="gate-email" /></div>
              </div>
              <div>
                <label className="block text-xs text-[#B9C8D5] mb-1">Mobile number * <span className="text-[10px] text-[#B9C8D5]/70">(for delivery updates)</span></label>
                <ThemedPhoneInput value={gateContact.phone} onChange={(v) => setGateContact((c) => ({ ...c, phone: v }))} data-testid="gate-phone" />
              </div>
              {gateError && <p className="text-sm text-[#F4A93A]" data-testid="gate-error">{gateError}</p>}
              <button type="submit" disabled={!allFilled} className="w-full btn-primary flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="gate-continue">
                Continue to booking <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
        <Fireflies count={20} />
        <div className="glass p-10 text-center relative z-10" data-testid="checkout-loading">
          <img src={LOGO_URL} alt="" className="h-14 mx-auto mb-5" style={{ filter: "url(#knockoutBlack)" }} />
          <p className="font-serif text-2xl">Summoning fireflies to your checkout…</p>
        </div>
      </div>
    );
  }

  if (error === "no-trip" || error === "no-items" || !trip) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass p-8 max-w-md text-center" data-testid="checkout-missing-state">
          <h1 className="font-serif text-3xl text-[#FFD166] mb-3">Nothing to check out</h1>
          <p className="text-sm text-[#B9C8D5] mb-6">Pick your dates, delivery and some gear first, then come back here to pay.</p>
          <Link to="/" className="btn-primary" data-testid="checkout-back-home">Back to catalog</Link>
        </div>
      </div>
    );
  }

  if (error || !clientSecret) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass p-8 max-w-md text-center" data-testid="checkout-error">
          <h1 className="font-serif text-3xl text-[#FFD166] mb-3">Something drifted off</h1>
          <p className="text-sm text-[#B9C8D5] mb-6">{typeof error === "string" ? error : "Could not start your checkout."}</p>
          <Link to="/" className="btn-ghost" data-testid="checkout-back-home">Back to catalog</Link>
        </div>
      </div>
    );
  }

  const appearance = {
    theme: "night",
    variables: {
      colorPrimary: "#FFD166",
      colorBackground: "#0F1E2C",
      colorText: "#F6F1E4",
      colorDanger: "#F4A93A",
      fontFamily: "system-ui, sans-serif",
      borderRadius: "12px",
    },
  };

  return (
    <div className="min-h-screen bg-[#04090F] no-custom-cursor relative overflow-hidden">
      <Fireflies count={12} />
      <div className="relative z-10">
        <header className="border-b border-[#FFD166]/15 bg-[#0A1A26]/60 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-[#FFD166] hover:text-[#F4A93A] text-sm" data-testid="checkout-back-link">
              <ChevronLeft className="w-4 h-4" /> Back to catalog
            </Link>
            <img src={LOGO_URL} alt="Enchanted Gear Rentals" className="h-10" style={{ filter: "url(#knockoutBlack)" }} />
            <div className="w-24 text-right text-[10px] uppercase tracking-widest text-[#B9C8D5]">Secure checkout</div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
          <h1 className="font-serif text-4xl sm:text-5xl text-[#FFD166] mb-2">Confirm &amp; pay</h1>
          <p className="text-sm text-[#B9C8D5] mb-8">Review your protection add-ons, agree to SMS + Terms, and pay securely.</p>

          <div className="grid lg:grid-cols-[1fr_420px] gap-6 lg:gap-8">
            <ErrorBoundary name="checkout-form" title="Something went wrong loading the checkout form." message="Your cart and trip details are saved. Tap Try again, or refresh the page.">
            <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
              <CheckoutForm
                reservationId={reservationId}
                products={products}
                quote={quote}
                setQuote={setQuote}
                trip={trip}
                zones={zones}
                settings={settings}
                resumeCustomer={resumeCustomer}
                gateContact={gateContact}
              />
            </Elements>
            </ErrorBoundary>
            <ErrorBoundary name="order-summary" title="Something went wrong showing your order summary.">
            <OrderSummary
              products={products}
              quote={quote}
              trip={trip}
              zones={zones}
              reservationId={reservationId}
              setQuote={setQuote}
              onTripSaved={(patch) => setTripPatch(patch)}
              addressLocked={!!resumeCustomer?.address_locked}
            />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CheckoutPage;
